import React, { useState, useContext, Fragment, useEffect } from 'react';
import { FeedArea } from '@go-shared/types/feedTemplate';
import FeedEditorRow from './FeedEditorRow';
import {
  hotspotTarget,
  findContentTemplate,
  collectTarget,
  findEventTemplate,
  findAllEvents,
} from '@go-client/lib/feedEditor';
import {
  BareContentTemplateLegacy,
  ContentTemplateParams,
  FeedEditorElementType,
} from '@go-shared/types/contentTemplate';
import './FeedEditorLayoutComponent.scss';
import { DropTarget } from 'react-dnd';
import { CONTENT_DND_TYPE } from '@go-client/components/DraggableContentItem';
import {
  useContentTemplates,
  useContentTemplatesLegacy,
  useEvents,
  useEventTemplates,
  useVenueTemplates,
} from '@go-client/hooks/feedEditor';
import FeedEditorText from './Text/FeedEditorText';
import { VenuesRundownHotspot } from '../VenueRundown/VenueRundownHotspot';
import { VenueMealPeriodsHotspot } from '../VenueMealPeriods/VenueMealPeriodsHotspot';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { SailingDateWidget } from '../Widgets/SailingDateWidget/SailingDateWidget';
import {
  generateItineraryWidgetContentTemplate,
  generateSailingDayWidgetContentTemplate,
} from '@go-shared/lib/feed';
import { ClickAway } from '@go-client/components/UIkit/ClickAway/ClickAway';
import { BareSailingDay, SailingDayBundle } from '@go-shared/types/sailing';
import { Property } from '@go-shared/types/property';
import { WellnessRundown } from '../WellnessRundown';
import { FilteredRundown } from '../FilteredRundown';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { Language } from '@go-shared/types/info';
import { evaluateMergedVars } from '@go-shared/lib/mergedVars';
import { DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT, timeToString } from '@go-shared/lib/time';
import { DEFAULT_DAY_START, getHourFormat } from '@go-shared/lib/property';
import { populate as populateSailing, populateDay } from '@go-shared/lib/itinerary';
import { DailyProgram } from '@go-shared/types/dailyProgram';
import { makeSailingDayBundle } from '@go-shared/lib/sailing';
import { BubbleButton } from '@go-client/components/BubbleUI';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import { ItineraryWidget } from '../Widgets/ItineraryWidget/ItineraryWidget';
import { safeIdCompare } from '@go-shared/lib/utils';
import { Event } from '@go-shared/types/event';
import { convertLegacyContentTemplateToReal, populateContentTemplate } from '@go-shared/lib/contentTemplate';
import { MultiDay } from '../MultiDay';
import { BrandDateFormat } from '@go-shared/types/brand';
import { EventRundown } from '../EventRundown';

interface FeedEditorColumnProps {
  parent: FeedArea;
  onRowCreated: (area: FeedArea, idx: number, newArea: FeedArea) => void;
  onRowMove: (area: FeedArea, idx: number, direction: 1 | -1) => void;
  onColumnCreated: (area: FeedArea, idx: number, newArea: FeedArea) => void;
  parentIdx: number;
  childCount?: number;
  onDrop?: (contentTemplate: BareContentTemplateLegacy) => void;
  connectDropTarget?: any;
  isOver?: boolean;
  highlightAreas: boolean;
  selectedLanguage: Language | undefined;
  showMergedVars: boolean;
  onClickAgain?: () => void;
  onContentChange: (area: FeedArea, contentTemplate: BareContentTemplateLegacy) => void;
  onElementCreated: (area: FeedArea, elementType: FeedEditorElementType) => void;
  onAreaDeleted: (area: FeedArea) => void;
  onElementChanged: (area: FeedArea) => void;
  activeElem?: string;
  setActiveElement?: (id: string) => void;
  editorLocked: boolean;
  dailyProgram: DailyProgram;
  mcmKey: string;
}

const FeedEditorColumn: React.FC<FeedEditorColumnProps> = ({
  parent,
  parentIdx,
  onColumnCreated,
  onRowCreated,
  onRowMove,
  connectDropTarget,
  onContentChange,
  isOver,
  highlightAreas,
  onElementCreated,
  onAreaDeleted,
  onElementChanged,
  selectedLanguage,
  showMergedVars,
  onClickAgain,
  childCount,
  activeElem,
  setActiveElement,
  editorLocked,
  dailyProgram,
  mcmKey,
}) => {
  const { languages, defaultLanguage, brand, settings } = useServerInfo();
  const cts = useContentTemplatesLegacy();
  const ctsReal = useContentTemplates();
  let ets = useEventTemplates();
  const vts = useVenueTemplates();
  const events = useEvents();
  const { sailing, sailingDay, property, setSettingsPanel, kbEvent, setSelectedContent } =
    useContext(FeedEditorCtx);
  const [mouseOver, setMouseOver] = useState(false);

  let hourFormat = DEFAULT_HOUR_FORMAT;
  let levelLabel = dailyProgram.collections.commonTexts.deck;
  if (property) {
    hourFormat = getHourFormat(property);
  }

  const fullSailing = sailing && property ? populateSailing(sailing, property, [], []) : undefined;
  const fullSailingDay = sailingDay ? populateDay(sailingDay, []) : undefined;
  const sailingDayBundle =
    fullSailing && fullSailingDay ? makeSailingDayBundle(fullSailing, fullSailingDay) : undefined;

  const [menuVisible, setMenuVisible] = useState(false);
  const allowEdit = selectedLanguage === defaultLanguage;

  useEffect(() => {
    if (activeElem != parent._id) {
      setMenuVisible(false);
      setSettingsPanel(undefined);
    } else {
      setMenuVisible(true);
    }
  }, [activeElem]);

  useEffect(() => {
    if (menuVisible && setActiveElement) {
      setActiveElement(parent._id);
    }
  }, [menuVisible]);

  const width = parent.children ? (100 / parent.children.length).toString() + '%' : '100%';
  let matchedTemplate: BareContentTemplateLegacy | undefined;
  const passedProps = {
    width,
    onRowCreated,
    onRowMove,
    onColumnCreated,
    onContentChange,
    onElementCreated,
    onAreaDeleted,
    onElementChanged,
    sailingDay: sailingDay!,
    property: property!,
    sailingDayBundle,
    selectedLanguage,
    showMergedVars,
    highlightAreas,
    onClickAgain,
    editorLocked,
  };

  // Areas hosting one of these widgets are containers, not content blurbs. A stale `content`
  // left over from before the widget was assigned must not be treated as the area's matched
  // template, or it wins over the widget in edit-mode routing and rendering.
  const hasWidgetChildren =
    !!parent.children?.[0] &&
    ['event-rundown', 'venue-rundown', 'venue-meal-periods', 'multi-day'].includes(
      parent.children[0].contentType
    );

  if (parent.content && !hasWidgetChildren) {
    if (parent.content.params && parent.content.params.eventId) {
      if (!selectedLanguage || selectedLanguage === defaultLanguage) {
        ets = ets.filter(et => !et.notNative);
      }

      const evt = findEventTemplate(parent.content.params.eventId, ets);
      const allEvts = findAllEvents(
        parent.content.params.eventId,
        events,
        property?.dayStart ?? DEFAULT_DAY_START
      );
      const event: Event | undefined =
        allEvts[(parent.content as ContentTemplateParams).eventTemplateInstance ?? 0] || allEvts[0];

      let imageDiv = '';
      let attributesDiv = '';
      let startTimesDiv = '';
      let locationDiv = '';
      let levelDiv = '';
      let locationAndLevelDiv = '';
      let titleDiv = '';
      let bodyDiv = '';

      if (evt && event) {
        if (event.imageOverride || evt.images[0]) {
          imageDiv = `<div class="EventBlurbImage hidden"><img src="${
            event.imageOverride || evt.images[0]
          }" /></div>\n`;
        }

        const attributes = brand.attributes.filter(
          a => (evt.attributes || []).findIndex(b => safeIdCompare(a._id, b._id)) > -1
        );

        const eventHighlights = brand.eventHighlights.filter(
          a => (event.highlights || []).findIndex(b => safeIdCompare(a._id, b._id)) > -1
        );

        attributesDiv = `
          <div class="EventBlurbAttributes hidden">
          
            ${[
              attributes.map(a => {
                let imgDiv = '';
                let nameDiv = `<span class="EventBlurbAttribute_${a.name}"><span>${a.name}</span></span>`;
                if (a.iconUrl) {
                  imgDiv = `<img src="${a.iconUrl}"`;
                }
                return `${imgDiv}${nameDiv}`;
              }),
              eventHighlights.map(a => {
                return `<span class="EventBlurbHighlight_${a.name}"><span>${a.name}</span></span>`;
              }),
            ].join('')}
          </div>\n`;
        startTimesDiv = `
          <div class="EventBlurbStartTimes hidden">${allEvts
            .map(e => timeToString(e.startTime, hourFormat, DEFAULT_TIMESTR_FORMAT))
            .map(e => `<span>${e}</span>`)
            .join('')}
          </div>\n`;
        locationDiv = `<div class="EventBlurbLocation hidden">${event?.venue.name}</div>\n`;
        levelDiv = `
          <div class="EventBlurbLevel hidden">
            <span class="EventBlurbLevelLabel">${levelLabel}</span>
            ${event?.venue.level}
          </div>\n`;
        locationAndLevelDiv = `<div class="EventBlurbLocationLevel">${locationDiv}${levelDiv}</div>`;
        titleDiv = `<h2 class="EventBlurbHeader">${evt.title}</h2>\n`;
        bodyDiv = `<p class="EventBlurbContent">${evt.description}</p>`;
      }

      const eventBlurbNoContainer = `${imageDiv}${attributesDiv}${startTimesDiv}${locationAndLevelDiv}${titleDiv}${bodyDiv}`;
      const containerEventBlurb = `<div class="EventBlurbContainer">${eventBlurbNoContainer}</div>`;

      parent.content.content = settings.USE_EVENT_BLURB_CONTAINER
        ? containerEventBlurb
        : eventBlurbNoContainer;
      matchedTemplate = parent.content;

      const [newContent] = evaluateMergedVars(
        parent.content.content,
        sailingDayBundle,
        languages.map(l => l.key),
        selectedLanguage?.key,
        defaultLanguage.key,
        event?.mergedVars
      );

      (matchedTemplate as any)._evaluatedContent = newContent;
      (matchedTemplate as any)._hasMergedVars = false;
      (matchedTemplate as any)._isContentTemplate = false;
    } else {
      const ct = findContentTemplate(parent.content.content, cts);
      matchedTemplate = ct;

      if (ct) {
        const [newContent, mergedVars] = evaluateMergedVars(
          ct.content,
          sailingDayBundle,
          languages.map(l => l.key),
          selectedLanguage?.key,
          defaultLanguage.key,
          parent.content as any
        );

        (matchedTemplate as any)._evaluatedContent = newContent;
        (matchedTemplate as any)._hasMergedVars = mergedVars.length > 0;
        (matchedTemplate as any)._isContentTemplate = true;
      }
    }
  }

  const getHeight = () => {
    if (!highlightAreas) return 'auto';
    if (matchedTemplate && !matchedTemplate.content) return 25;
    if (matchedTemplate) return 10;
    if (!parent.children) return 25;
    if (parent.children && parent.children.length) return 'auto';
  };

  const renderComponent = () => {
    let style = Object.assign(
      {
        minHeight: getHeight(),
      },
      parent.style
    );

    const hasMergedVars = matchedTemplate && (matchedTemplate as any)._hasMergedVars;
    const vtsMap = mapBy(vts, '_id');

    const isContentBlurbEdit =
      matchedTemplate &&
      (matchedTemplate as any)._isContentTemplate &&
      (matchedTemplate as any)._hasMergedVars;
    const isEventBlurbEdit = matchedTemplate && !(matchedTemplate as any)._isContentTemplate;

    const isVenueEdit =
      parent.children && parent.children[0] && parent.children[0].contentType === 'venue-rundown';
    const isMealEdit =
      parent.children &&
      parent.children[0] &&
      parent.children[0].contentType === 'venue-meal-periods';
    const isMultiDayEdit = parent.children?.[0]?.contentType === 'multi-day';
    const isEventRundownEdit =
      parent.children && parent.children[0] && parent.children[0].contentType === 'event-rundown';

    const isAnyEdit =
      isContentBlurbEdit ||
      isEventBlurbEdit ||
      isVenueEdit ||
      isMealEdit ||
      isMultiDayEdit ||
      isEventRundownEdit;

    if (isOver) {
      (style as any).backgroundColor = '#0000001A';
      (style as any).border = '6px solid #00000066';
    } else if (highlightAreas) {
      if (hasMergedVars || isAnyEdit) {
        // blue signifies the edit button is available
        (style as any).backgroundColor = '#0000FF1A';
        (style as any).border = '1px solid #0000FF66';
      } else {
        // regular editable column
        (style as any).backgroundColor = '#FF00001A';
        (style as any).border = '1px solid #FF000066';
      }
    }

    function handleEditClick() {
      if (editorLocked) return;

      if (isEventBlurbEdit) {
        setSettingsPanel({
          type: 'event-settings',
          feedArea: parent,
          contentTemplate: matchedTemplate!,
        });
      } else if (isContentBlurbEdit) {
        setSettingsPanel({
          type: 'content-settings',
          feedArea: parent,
          contentTemplate: matchedTemplate!,
        });
      } else if (isVenueEdit) {
        setSettingsPanel({
          type: 'venue-rundown-settings',
          venueRundownId: parent.children![0].content!._id,
          feedArea: parent.children![0],
          selectedVenues: (parent.children![0].content!.params?.venueRundownConfig! || [])
            .map(vid => vtsMap[vid])
            .filter(notUndefined),
          tab: 'venues',
        });
      } else if (isMealEdit) {
        setSettingsPanel({
          type: 'venue-meal-periods-settings',
          venueMealPeriodsId: parent.children![0].content!._id,
          feedArea: parent.children![0],
          selectedVenues: (parent.children![0].content!.params?.venueMealPeriodsConfig! || [])
            .map(vid => vtsMap[vid])
            .filter(notUndefined),
          tab: 'venues',
        });
      } else if (isMultiDayEdit) {
        setSettingsPanel({
          type: 'multi-day-settings',
          feedArea: parent.children![0],
        });
      } else if (isEventRundownEdit) {
        setSettingsPanel({
          type: 'event-rundown-settings',
          feedArea: parent.children![0],
          eventRundownId: parent.children![0].content!._id,
        });
      }
    }

    function handleDeleteClick() {
      if (parent.isLocked || editorLocked) return;
      onAreaDeleted(parent);
    }

    function handleOpenClick() {
      if (!matchedTemplate) return;

      const real = convertLegacyContentTemplateToReal(ctsReal, matchedTemplate);
      if (!real) return;

      setSelectedContent({
        type: 'edit',
        edit: populateContentTemplate(real, brand),
      });
    }

    useEffect(() => {
      if (kbEvent?.key === 'Enter') {
        if (menuVisible) {
          handleEditClick();
        }
      }

      if (kbEvent?.key === 'Backspace') {
        if (menuVisible) {
          handleDeleteClick();
        }
      }
    }, [kbEvent]);

    return (
      <div className="w-full">
        <ClickAway onClickAway={() => setMenuVisible(false)} className={parent.className}>
          <div
            style={style}
            className={`feed-column rounded ${parent.className} relative cursor-pointer ${
              menuVisible || mouseOver ? 'outline-column' : ''
            }`}
            onClick={() => {
              if (menuVisible) {
                setMenuVisible(false);
                if (onClickAgain) onClickAgain();
              } else {
                setMenuVisible(true);
              }
            }}
            onMouseOver={e => {
              e.stopPropagation();
              setMouseOver(true);
            }}
            onMouseOut={() => {
              setMouseOver(false);
            }}
          >
            {matchedTemplate && (
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    (showMergedVars ? (matchedTemplate as any)._evaluatedContent : undefined) ||
                    matchedTemplate.content,
                }}
              />
            )}
            {parent.children &&
              parent.children.map((child, i) => (
                <Fragment key={i}>
                  {getFeedLayoutComponent(
                    child,
                    passedProps,
                    i,
                    dailyProgram,
                    brand.dateFormat,
                    mcmKey
                  )}
                </Fragment>
              ))}
            <div
              className={`column-layout-actions ${
                parent.isLocked ? 'bg-grey-dark' : 'bg-red'
              } px-2 inline-flex items-center ${menuVisible ? 'visible' : ''}`}
              style={{
                pointerEvents: menuVisible ? 'all' : 'none',
                opacity: menuVisible ? 1 : 0,
              }}
            >
              <span className="text-white text-base font-bold mr-3">COLUMN</span>
              <div className="ml-auto"></div>
              {parentIdx != 0 && !editorLocked && (
                <BubbleButton
                  variant="white"
                  inverse
                  className="text-lg px-2-i"
                  onClick={() => {
                    onRowMove(parent, parentIdx, -1);
                  }}
                  icon="arrow-left"
                />
              )}
              {parentIdx != (childCount || 0) - 1 && !editorLocked && (
                <BubbleButton
                  variant="white"
                  inverse
                  className="text-lg px-2-i"
                  onClick={() => {
                    onRowMove(parent, parentIdx, 1);
                  }}
                  icon="arrow-right"
                />
              )}
              {!editorLocked &&
                matchedTemplate &&
                (matchedTemplate as any)._isContentTemplate &&
                allowEdit && (
                  <BubbleButton
                    variant="white"
                    inverse
                    className="action-2 px-2-i text-lg"
                    icon="arrow-up-right"
                    onClick={() => handleOpenClick()}
                  />
                )}
              {isAnyEdit && !editorLocked && (
                <BubbleButton
                  variant="white"
                  inverse
                  className="action-2 px-2-i text-lg"
                  icon="dollar-sign"
                  onClick={() => handleEditClick()}
                />
              )}
              <BubbleButton
                variant="white"
                inverse
                className="action-2 px-2-i text-lg"
                disabled={parent.isLocked || editorLocked}
                icon={parent.isLocked || editorLocked ? 'lock' : 'trash-alt'}
                onClick={() => handleDeleteClick()}
              />
            </div>
          </div>
        </ClickAway>
      </div>
    );
  };

  if (parent.children && !matchedTemplate) return renderComponent();
  return connectDropTarget(renderComponent());
};

export default DropTarget<FeedEditorColumnProps>(
  CONTENT_DND_TYPE,
  hotspotTarget,
  collectTarget
)(FeedEditorColumn);

interface FeedLayoutComponentNeededProps {
  width: string;
  onContentChange: (area: FeedArea, contentTemplate: BareContentTemplateLegacy) => void;
  onRowCreated: (area: FeedArea, idx: number, newArea: FeedArea) => void;
  onRowMove: (area: FeedArea, idx: number, direction: 1 | -1) => void;
  onColumnCreated: (area: FeedArea, idx: number, newArea: FeedArea) => void;
  onElementCreated: (area: FeedArea, elementType: FeedEditorElementType) => void;
  onAreaDeleted: (area: FeedArea) => void;
  onElementChanged: (area: FeedArea) => void;
  onClickAgain?: () => void;
  sailingDay: BareSailingDay;
  property: Property;
  sailingDayBundle: SailingDayBundle | undefined;
  selectedLanguage: Language | undefined;
  showMergedVars: boolean;
  highlightAreas: boolean;
  editorLocked: boolean;
}

export const getFeedLayoutComponent = (
  area: FeedArea,
  props: FeedLayoutComponentNeededProps,
  index: number,
  dailyProgram: DailyProgram,
  dateFormat: BrandDateFormat,
  mcmKey: string
) => {
  switch (area.contentType) {
    case 'column': {
      if (!(area.style as any).width) {
        (area.style as any) = props.width;
      }

      return (
        <FeedEditorColumn
          key={area._id}
          {...props}
          parent={area}
          parentIdx={index}
          onDrop={ct => props.onContentChange(area, ct)}
          selectedLanguage={props.selectedLanguage}
          onClickAgain={props.onClickAgain}
          editorLocked={props.editorLocked}
          dailyProgram={dailyProgram}
          mcmKey={mcmKey}
        />
      );
    }
    case 'venue-rundown': {
      return (
        <VenuesRundownHotspot
          key={area._id}
          area={area}
          {...props}
          contentTemplate={area.content as any}
          commonTexts={dailyProgram.collections.commonTexts}
          mcmKey={mcmKey}
        />
      );
    }
    case 'venue-meal-periods': {
      return (
        <VenueMealPeriodsHotspot
          key={area._id}
          area={area}
          {...props}
          contentTemplate={area.content as any}
          commonTexts={dailyProgram.collections.commonTexts}
          mcmKey={mcmKey}
        />
      );
    }
    case 'filtered-rundown': {
      return <FilteredRundown area={area} selectedLanguage={props.selectedLanguage} />;
    }
    case 'wellness-rundown': {
      return <WellnessRundown />;
    }
    case 'event-rundown': {
      return <EventRundown mcmKey={mcmKey} area={area} selectedLanguage={props.selectedLanguage} />;
    }

    case 'row': {
      return (
        <FeedEditorRow
          key={area._id}
          {...props}
          parent={area}
          parentIdx={index}
          onDrop={ctn => props.onContentChange(area, ctn)}
          selectedLanguage={props.selectedLanguage}
          dailyProgram={dailyProgram}
          mcmKey={mcmKey}
        />
      );
    }
    case 'text': {
      return (
        <FeedEditorText
          key={area._id}
          parent={area}
          onContentChange={props.onContentChange}
          onElementChanged={props.onElementChanged}
        />
      );
    }
    case 'itinerary-widget': {
      let generatedArea = area;
      if (props.sailingDay && props.property) {
        area.content = generateItineraryWidgetContentTemplate(
          props.property,
          props.sailingDayBundle
        );
      }

      return (
        <div
          key={area._id}
          className={area.className}
          dangerouslySetInnerHTML={{ __html: ItineraryWidget.getHTML(generatedArea) }}
        />
      );
    }
    case 'date-widget': {
      let generatedArea = area;
      if (props.sailingDay && props.property) {
        area.content = generateSailingDayWidgetContentTemplate(
          props.property,
          dateFormat,
          props.sailingDay.date,
          props.selectedLanguage?.key
        );
      }

      return (
        <div
          key={area._id}
          className={area.className}
          dangerouslySetInnerHTML={{ __html: SailingDateWidget.getHTML(generatedArea) }}
        />
      );
    }
    case 'multi-day': {
      return (
        <MultiDay
          selectedLanguage={props.selectedLanguage}
          area={area}
          showMergedVars={props.showMergedVars}
        />
      );
    }
    default: {
      return <React.Fragment key={area._id}></React.Fragment>;
    }
  }
};
